import React, { useMemo, useState } from 'react';
import { SlotApiConfig } from '../../CustomBlocks/slotBooking/slotTypes';
import { getUrlPlaceholders } from '../../CustomBlocks/slotBooking/slotUtils';
import { CalendarIcon, ErrorIcon, SuccessIcon } from '../../shared/Icons';
import { SettingsGroup, SettingsRow, Switch } from './settingsUi';
import { PrimaryButton, StatusBanner, TILE } from './SlotBookingEditor.styles';
import { useSlotApiTest } from './useSlotApiTest';

interface ApiSourceSectionProps {
  value: SlotApiConfig;
  onChange: (patch: Partial<SlotApiConfig>) => void;
}

const ApiSourceSection: React.FC<ApiSourceSectionProps> = ({ value, onChange }) => {
  const apiTest = useSlotApiTest();
  const [testValues, setTestValues] = useState<Record<string, string>>({});
  const placeholders = useMemo(() => getUrlPlaceholders(value.url), [value.url]);

  const change = (patch: Partial<SlotApiConfig>) => {
    apiTest.reset();
    onChange(patch);
  };

  return (
    <>
      <SettingsGroup
        title="Endpoint"
        footer={
          <>
            To use a value from the form link, write <code>{'{{webinarId}}'}</code> in the URL and open
            the form with <code>?webinarId=…</code>.
          </>
        }
      >
        <SettingsRow stacked label="API URL" description="GET endpoint that returns the available slot times.">
          <input
            type="url"
            className="sb-text-input"
            placeholder="https://example.com/api/schedule/{{webinarId}}"
            value={value.url}
            onChange={(e) => change({ url: e.target.value })}
            spellCheck={false}
            aria-label="API URL"
          />
        </SettingsRow>
        <SettingsRow
          stacked
          label="Slots key in response"
          description={
            <>
              List name in the JSON, e.g. <code>schedule</code>. Use dots for nested keys like{' '}
              <code>data.slots</code>.
            </>
          }
        >
          <input
            type="text"
            className="sb-text-input"
            placeholder="schedule"
            value={value.path}
            onChange={(e) => change({ path: e.target.value })}
            spellCheck={false}
            aria-label="Slots key in response"
          />
        </SettingsRow>
      </SettingsGroup>

      <SettingsGroup title="Request">
        <SettingsRow
          icon={<CalendarIcon size={16} />}
          iconColor={TILE.indigo}
          label="Send visible month"
          description={
            <>
              Adds <code>filter[startAt]</code> and <code>filter[endAt]</code>
            </>
          }
        >
          <Switch
            checked={value.sendRange}
            label="Send visible month"
            onChange={(sendRange) => change({ sendRange })}
          />
        </SettingsRow>
      </SettingsGroup>

      {placeholders.length > 0 && (
        <SettingsGroup title="Test values" footer="Only used by Test connection. Not saved.">
          {placeholders.map((key) => (
            <SettingsRow key={key} stacked label={key}>
              <input
                type="text"
                className="sb-text-input"
                placeholder={`A real ${key}`}
                value={testValues[key] ?? ''}
                onChange={(e) => {
                  apiTest.reset();
                  setTestValues((prev) => ({ ...prev, [key]: e.target.value }));
                }}
                aria-label={`Test value for ${key}`}
              />
            </SettingsRow>
          ))}
        </SettingsGroup>
      )}

      <PrimaryButton
        type="button"
        disabled={apiTest.running}
        onClick={() => apiTest.run(value, testValues)}
      >
        {apiTest.running ? 'Testing connection…' : 'Test connection'}
      </PrimaryButton>

      {apiTest.title && (
        <StatusBanner $ok={apiTest.ok} role="status">
          {apiTest.ok ? <SuccessIcon /> : <ErrorIcon />}
          <div className="sb-status-body">
            <div className="sb-status-title">{apiTest.title}</div>
            {apiTest.message && <div>{apiTest.message}</div>}
            {apiTest.samples.length > 0 && (
              <div className="sb-status-samples">
                {apiTest.samples.map((sample) => (
                  <span key={sample} className="sb-status-sample">{sample}</span>
                ))}
              </div>
            )}
          </div>
        </StatusBanner>
      )}
    </>
  );
};

export default ApiSourceSection;
